<app-client-layout>
  <div class="container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="page-title-box d-flex align-items-center justify-content-between">
          <h4 class="mb-0">Client Dashboard</h4>
          <div class="page-title-right">
            <ol class="breadcrumb m-0">
              <li class="breadcrumb-item"><a href="javascript: void(0);">Dashboard</a></li>
              <li class="breadcrumb-item active">Client</li>
            </ol>
          </div>
        </div>
      </div>
    </div>

    <!-- Client Info -->
    <div class="row" *ngIf="client">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title">Welcome, {{ client.name }}</h4>
            <p class="card-text">Company: {{ client.company }}</p>
            <p class="card-text">Email: {{ client.email }}</p>
            <p class="card-text">Account Status: 
              <span class="badge" [ngClass]="{
                'badge-success': client.status === 'active',
                'badge-warning': client.status === 'inactive',
                'badge-danger': client.status === 'suspended'
              }">{{ client.status }}</span>
            </p>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Error Message when client not found -->
    <div class="row" *ngIf="!client && error">
      <div class="col-12">
        <div class="alert alert-danger" role="alert">
          {{ error }}
        </div>
        
        <!-- Debug Information -->
        <div class="card">
          <div class="card-body">
            <h5 class="card-title">Debug Information</h5>
            <p class="card-text">Logged in username: {{ loggedInUsername || 'Not available' }}</p>
            <p class="card-text">Please contact your system administrator to ensure your account is properly configured.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading Indicator -->
    <div class="row" *ngIf="loading">
      <div class="col-12">
        <div class="card">
          <div class="card-body text-center">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading your dashboard...</p>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Stats Cards -->
    <div class="row" *ngIf="!loading && (client || error)">
      <div class="col-md-6 col-xl-3">
        <div class="card">
          <div class="card-body">
            <div class="float-end mt-2">
              <div id="total-vms-chart"></div>
            </div>
            <div>
              <h4 class="mb-1 mt-1"><span data-plugin="counterup">{{ totalVMs }}</span></h4>
              <p class="text-muted mb-0">Total VMs</p>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-6 col-xl-3">
        <div class="card">
          <div class="card-body">
            <div class="float-end mt-2">
              <div id="running-vms-chart"></div>
            </div>
            <div>
              <h4 class="mb-1 mt-1"><span data-plugin="counterup">{{ runningVMs }}</span></h4>
              <p class="text-muted mb-0">Running VMs</p>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-6 col-xl-3">
        <div class="card">
          <div class="card-body">
            <div class="float-end mt-2">
              <div id="stopped-vms-chart"></div>
            </div>
            <div>
              <h4 class="mb-1 mt-1"><span data-plugin="counterup">{{ stoppedVMs }}</span></h4>
              <p class="text-muted mb-0">Stopped VMs</p>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-6 col-xl-3">
        <div class="card">
          <div class="card-body">
            <div class="float-end mt-2">
              <div id="paused-vms-chart"></div>
            </div>
            <div>
              <h4 class="mb-1 mt-1"><span data-plugin="counterup">{{ pausedVMs }}</span></h4>
              <p class="text-muted mb-0">Paused VMs</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h4 class="card-title">Your Virtual Machines</h4>
              <div>
                <button type="button" class="btn btn-primary waves-effect waves-light me-2" (click)="onAddVM()">
                  <i class="mdi mdi-plus me-1"></i> Add VM
                </button>
                <button type="button" class="btn btn-outline-secondary waves-effect waves-light" (click)="refreshData()">
                  <i class="mdi mdi-refresh me-1"></i> Refresh
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- VMs Table -->
    <div class="row" *ngIf="client && !error">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="table-responsive" *ngIf="clientVMs.length > 0; else noVMs">
              <table class="table table-centered table-nowrap mb-0">
                <thead class="table-light">
                  <tr>
                    <th>VM Name</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>CPU</th>
                    <th>Memory</th>
                    <th>Disk</th>
                    <th>IP Address</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let vm of clientVMs">
                    <td>
                      <h5 class="font-size-14 mb-1">{{ vm.name }}</h5>
                      <p class="text-muted mb-0">{{ vm.id }}</p>
                    </td>
                    <td>{{ vm.type.toUpperCase() }}</td>
                    <td>
                      <span class="badge" [ngClass]="getVMStatusClass(vm.status)">{{ vm.status }}</span>
                    </td>
                    <td>{{ vm.cpu }} vCPU</td>
                    <td>{{ vm.memory }} MB</td>
                    <td>{{ vm.disk }} GB</td>
                    <td>{{ vm.ipAddress }}</td>
                    <td>
                      <div class="btn-group" role="group">
                        <button type="button" class="btn btn-outline-primary btn-sm waves-effect waves-light me-1" 
                                (click)="onViewVM(vm.id)" title="View Details">
                          <i class="mdi mdi-eye"></i>
                        </button>
                        <button type="button" class="btn btn-success btn-sm waves-effect waves-light me-1" 
                                *ngIf="vm.status !== 'running'" (click)="onStartVM(vm.id)" title="Start">
                          <i class="mdi mdi-play"></i>
                        </button>
                        <button type="button" class="btn btn-warning btn-sm waves-effect waves-light me-1" 
                                *ngIf="vm.status === 'running'" (click)="onStopVM(vm.id)" title="Stop">
                          <i class="mdi mdi-stop"></i>
                        </button>
                        <button type="button" class="btn btn-info btn-sm waves-effect waves-light" 
                                *ngIf="vm.status === 'running'" (click)="onRestartVM(vm.id)" title="Restart">
                          <i class="mdi mdi-restart"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <ng-template #noVMs>
              <div class="text-center py-5">
                <i class="mdi mdi-server-off mdi-48px text-muted mb-3"></i>
                <h5>No Virtual Machines Found</h5>
                <p class="text-muted">You don't have any virtual machines assigned to your account yet.</p>
                <button type="button" class="btn btn-primary waves-effect waves-light" (click)="onAddVM()">
                  <i class="mdi mdi-plus me-1"></i> Request New VM
                </button>
              </div>
            </ng-template>
          </div>
        </div>
      </div>
    </div>

    <!-- Success/Error Messages -->
    <div class="row" *ngIf="successMessage || error">
      <div class="col-12">
        <div class="alert" [ngClass]="{
          'alert-success': successMessage,
          'alert-danger': error
        }" role="alert">
          {{ successMessage || error }}
        </div>
      </div>
    </div>
  </div>
</app-client-layout>